import { formatDate, formatMoney } from '@/utils/format';
import type { ContractStatus, ContractToken } from '@/types/portal';

/** The agency name stamped into every generated contract. */
const AGENCY_NAME = 'Twiva';

/**
 * Every dynamic field the platform can fill inside a contract. Brands drop these
 * tokens into their own document and the platform resolves them per creator.
 */
export const contractTokens: ContractToken[] = [
  { token: '{{creator}}', label: 'Creator name', description: 'The creator this contract is issued to' },
  { token: '{{handle}}', label: 'Creator handle', description: "The creator's social handle" },
  { token: '{{brand}}', label: 'Brand', description: 'The brand the campaign is running for' },
  { token: '{{campaign}}', label: 'Campaign', description: 'The campaign name' },
  { token: '{{agency}}', label: 'Agency', description: 'Your agency (the contracting party)' },
  { token: '{{amount}}', label: 'Fee amount', description: "The creator's agreed fee, with currency" },
  { token: '{{deliverables}}', label: 'Deliverables', description: 'What the creator is expected to produce' },
  { token: '{{contentTypes}}', label: 'Content formats', description: 'Formats the creator will deliver' },
  { token: '{{startDate}}', label: 'Start date', description: 'Campaign start date' },
  { token: '{{endDate}}', label: 'End date', description: 'Campaign end date' },
  { token: '{{today}}', label: 'Issue date', description: 'The date the contract is issued' },
];

/** The wording a brand lands on before they upload their own agreement. */
export const defaultContractBody = `CREATOR COLLABORATION AGREEMENT

This Agreement is made between {{agency}} ("the Agency") and {{creator}} ({{handle}}) ("the Creator"), in respect of the {{campaign}} campaign for {{brand}} ("the Brand").

1. ENGAGEMENT
The Creator is engaged to produce and publish the following deliverables across {{contentTypes}} for the campaign running from {{startDate}} to {{endDate}}:
{{deliverables}}

2. FEE
In consideration of the services, the Agency will pay the Creator {{amount}}. Payment is released once the deliverables are approved and this Agreement has been signed by the Creator.

3. DELIVERABLES
The Creator will submit each deliverable by the agreed dates and will complete up to two rounds of revisions if requested.

4. CONTENT RIGHTS
The Brand receives a licence to reuse the approved content across paid and organic channels for twelve months.

5. TERM & TERMINATION
Either party may terminate on written notice. Deliverables already approved remain payable.

6. DIGITAL SIGNATURE
This Agreement takes effect on the date of the Creator's digital signature. The Creator cannot be activated on the campaign, and no fee is committed, until this Agreement is signed.

Issued {{today}} by {{agency}}.`;

export const defaultContractMeta = {
  fileName: 'Twiva Creator Collaboration Agreement.pdf',
  fileType: 'PDF',
  fileSize: '248 KB',
};

export interface ContractValuesInput {
  creatorName: string;
  handle: string;
  brand: string;
  campaign: string;
  currency: string;
  amount: number;
  deliverables: string | number;
  contentTypes: string;
  startDate: string;
  endDate: string;
}

/** Build the per-creator values used to resolve a contract's dynamic fields. */
export function buildContractValues(input: ContractValuesInput): Record<string, string> {
  return {
    creator: input.creatorName,
    handle: input.handle.startsWith('@') ? input.handle : `@${input.handle}`,
    brand: input.brand,
    campaign: input.campaign,
    agency: AGENCY_NAME,
    amount: formatMoney(input.amount, input.currency),
    deliverables: String(input.deliverables),
    contentTypes: input.contentTypes,
    startDate: input.startDate ? formatDate(input.startDate) : 'the agreed start date',
    endDate: input.endDate ? formatDate(input.endDate) : 'the agreed end date',
    today: formatDate(new Date().toISOString()),
  };
}

/** Replace every token in a contract body with its resolved value. */
export function resolveContract(body: string, values: Record<string, string>): string {
  return contractTokens.reduce((text, item) => {
    // Tokens look like {{creator}} — the key is the inner label.
    const key = item.token.slice(2, -2);
    const value = values[key];
    return value === undefined ? text : text.split(item.token).join(value);
  }, body);
}

/** The tokens that actually appear in a contract body. */
export function tokensInBody(body: string): ContractToken[] {
  return contractTokens.filter((item) => body.includes(item.token));
}

export interface ContractSummary {
  total: number;
  signed: number;
  awaiting: number;
  notSent: number;
  declined: number;
}

/** Group contracts into the headline buckets the brand cares about. */
export function summarizeContracts(
  contracts: Array<{ status: ContractStatus }>,
): ContractSummary {
  const summary: ContractSummary = {
    total: contracts.length,
    signed: 0,
    awaiting: 0,
    notSent: 0,
    declined: 0,
  };
  contracts.forEach((contract) => {
    if (contract.status === 'Signed') summary.signed += 1;
    else if (contract.status === 'Sent' || contract.status === 'Viewed') summary.awaiting += 1;
    else if (contract.status === 'Declined') summary.declined += 1;
    else summary.notSent += 1;
  });
  return summary;
}

/** A creator can only be activated once their contract is signed. */
export function isHireReady(status: ContractStatus | undefined): boolean {
  return status === 'Signed';
}

/** A file-safe name for a single resolved contract (its PDF's title). */
export function contractFileName(creatorName: string, campaign: string): string {
  const safe = `${creatorName} - ${campaign} - Contract`
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return safe;
}